/**
 * RefMarkerDecoration — TipTap extension that renders `[ref:kind:id]`
 * inline markers as visual Document Chips inside the Draft Canvas.
 *
 * Purely presentational: the underlying document text is untouched (so
 * exports, streaming reconciliation, and copy/paste all keep the raw marker
 * intact). Each marker gets two decorations:
 *   1. A widget with an icon-plus-label chip rendered at the marker's start.
 *   2. An inline decoration that visually collapses the raw `[ref:…]` text
 *      via `.ref-chip-hidden` (still selectable/copyable so exports keep it).
 *
 * Chip labels are resolved from two registries: the per-piece registry
 * (populated from intake_attachments — has `usage`/`body_excerpt`), then the
 * workspace-wide chip index (populated by `useCorpusChipResolver`).
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as PMNode } from "@tiptap/pm/model";
import {
  REF_MARKER_RE,
  getChipRegistry,
  resolveChip,
  resolveWorkspaceChip,
  subscribeChipRegistry,
} from "@/lib/refMarkers";

type Options = {
  /** The active piece id — used to look up resolved chips from the registry. */
  getPieceId: () => string | null;
};

const KIND_TO_ICON: Record<string, string> = {
  doc: "file",
  corpus: "file",
  document: "file",
  excerpts: "file",
  library: "file",
  notes: "file",
  podcasts: "file",
  folder: "folder",
  bucket: "layers",
};

// Minimal inline SVG icons matching the lucide set used by CorpusChipPill.
const ICON_SVG: Record<string, string> = {
  file: '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><polyline points="14 2 14 8 20 8"/></svg>',
  folder: '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/></svg>',
  layers: '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/></svg>',
};

const buildChipWidget = (
  label: string,
  kind: string,
  title: string,
  missing: boolean,
): HTMLElement => {
  const el = document.createElement("span");
  el.className = "ref-chip-widget" + (missing ? " ref-chip-widget--missing" : "");
  el.setAttribute("data-ref-kind", kind);
  el.setAttribute("title", title);
  el.contentEditable = "false";
  const icon = ICON_SVG[KIND_TO_ICON[kind.toLowerCase()] ?? "file"];
  el.innerHTML = `${icon}<span class="ref-chip-widget-label"></span>`;
  const labelSpan = el.querySelector(".ref-chip-widget-label") as HTMLSpanElement | null;
  if (labelSpan) labelSpan.textContent = label;
  return el;
};

const buildDecorations = (doc: PMNode, pieceId: string | null): DecorationSet | null => {
  const chips = getChipRegistry(pieceId);
  const decos: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    const text = node.text;
    const re = new RegExp(REF_MARKER_RE.source, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const kind = m[1];
      const id = m[2];
      const from = pos + m.index;
      const to = from + m[0].length;

      const pinned = resolveChip(kind, id, chips);
      const workspace = pinned ? null : resolveWorkspaceChip(kind, id);
      const resolvedLabel = pinned?.label ?? workspace?.label ?? null;
      const resolvedKind = (pinned?.kind ?? workspace?.kind ?? kind).toString();
      const missing = !resolvedLabel;

      const usage = pinned?.usage ?? "consult";
      const excerpt = pinned?.body_excerpt ?? "";
      const title = resolvedLabel
        ? `${resolvedLabel}${pinned ? ` · ${usage}` : ""}${excerpt ? `\n\n${excerpt}` : ""}`
        : `Unresolved reference — ${kind}:${id}`;

      const shortId = id.length > 8 ? `${id.slice(0, 8)}…` : id;
      const displayLabel = resolvedLabel ?? `${kind}:${shortId}`;

      decos.push(
        Decoration.widget(from, () => buildChipWidget(displayLabel, resolvedKind, title, missing), {
          side: -1,
          key: `refchip:${from}:${kind}:${id}:${displayLabel}`,
          ignoreSelection: true,
        }),
      );
      decos.push(
        Decoration.inline(from, to, {
          class: missing ? "ref-chip-hidden ref-chip-hidden--missing" : "ref-chip-hidden",
          "data-ref-kind": kind,
          "data-ref-id": id,
        }),
      );
    }
  });
  return decos.length > 0 ? DecorationSet.create(doc as never, decos) : null;
};

const key = new PluginKey("refMarkerDecoration");

export const RefMarkerDecoration = Extension.create<Options>({
  name: "refMarkerDecoration",

  addOptions() {
    return { getPieceId: () => null };
  },

  addProseMirrorPlugins() {
    const options = this.options;
    let currentPieceId = options.getPieceId();
    const plugin = new Plugin({
      key,
      state: {
        init: (_c, state) => buildDecorations(state.doc, currentPieceId),
        apply: (tr, old, _oldState, newState) => {
          const pieceId = options.getPieceId();
          const meta = tr.getMeta(key);
          if (!tr.docChanged && pieceId === currentPieceId && !meta) {
            return old ? old.map(tr.mapping, tr.doc as never) : null;
          }
          currentPieceId = pieceId;
          return buildDecorations(newState.doc, pieceId);
        },
      },
      props: {
        decorations(state) {
          return key.getState(state);
        },
      },
      view(view) {
        const unsubscribe = subscribeChipRegistry(() => {
          view.dispatch(view.state.tr.setMeta(key, { registryChanged: true }));
        });
        return { destroy: unsubscribe };
      },
    });
    return [plugin];
  },
});

export default RefMarkerDecoration;
